/**
 * Assignment Progress Matrix Tab Component
 * Shows user-course completion matrix
 */

import { useEffect, useState } from 'react'
import { Box, Typography, Skeleton } from '@mui/material'
import { useAppDispatch } from 'src/store/hooks'
import { fetchAssignmentUserProgressV4 } from 'src/v3/manage/manage.slice'
import { AssignmentIcon, CorrectIcon, IncorrectIcon } from 'src/v3/core/icons'

interface ProgressMatrixTabProps {
  assignment: any
  userProgress: any[]
  event: any
  isActive: boolean // Whether this tab is currently active
}

export default function ProgressMatrixTab({
  assignment,
  userProgress,
  event,
  isActive,
}: ProgressMatrixTabProps) {
  let dispatch = useAppDispatch()
  let [loadingMatrixDetails, setLoadingMatrixDetails] = useState(false)

  // Fetch detailed user progress when this tab becomes active
  useEffect(() => {
    if (isActive && event?.assignmentID) {
      // Check if we already have detailed data
      let hasDetailedData =
        userProgress.length > 0 &&
        userProgress.some(
          (user) => user.courseProgress && user.courseProgress.length > 0,
        )

      if (!hasDetailedData) {
        setLoadingMatrixDetails(true)
        dispatch(
          fetchAssignmentUserProgressV4({
            assignmentId: event.assignmentID,
            page: 1,
            pageSize: 100,
            includeDetails: true, // This will fetch course progress details
          }),
        ).finally(() => {
          setLoadingMatrixDetails(false)
        })
      }
    }
  }, [isActive, event?.assignmentID, dispatch, userProgress])

  return (
    <Box>
      <Typography
        variant="h6"
        sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <AssignmentIcon />
        User-Course Completion Matrix
      </Typography>

      {/* Matrix Legend */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mb: 3,
          justifyContent: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CorrectIcon sx={{ color: 'success.main', fontSize: 16 }} />
          <Typography variant="caption">Completed</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IncorrectIcon sx={{ color: 'error.main', fontSize: 16 }} />
          <Typography variant="caption">Not Completed</Typography>
        </Box>
      </Box>

      {/* Matrix Table */}
      <Box
        sx={{
          maxHeight: 350,
          overflow: 'auto',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        {userProgress.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: 'center', py: 4 }}
          >
            Load user data to view the progress matrix
          </Typography>
        ) : loadingMatrixDetails ? (
          <Box sx={{ minWidth: 600 }}>
            {/* Loading Skeleton for Matrix */}
            <Box
              sx={{
                display: 'flex',
                bgcolor: 'background.default',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box
                sx={{
                  minWidth: 200,
                  p: 1.5,
                  borderRight: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Skeleton variant="text" width="60%" height={20} />
              </Box>
              {assignment.courses.map((course: any, index: number) => (
                <Box
                  key={index}
                  sx={{
                    minWidth: 120,
                    p: 1.5,
                    textAlign: 'center',
                    borderRight:
                      index < assignment.courses.length - 1
                        ? '1px solid'
                        : 'none',
                    borderColor: 'divider',
                  }}
                >
                  <Skeleton
                    variant="text"
                    width="80%"
                    height={16}
                    sx={{ mx: 'auto' }}
                  />
                </Box>
              ))}
            </Box>
            {/* Loading skeleton rows */}
            {Array.from({ length: 5 }).map((_, rowIndex) => (
              <Box
                key={rowIndex}
                sx={{
                  display: 'flex',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box
                  sx={{
                    minWidth: 200,
                    p: 1.5,
                    borderRight: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Skeleton variant="text" width="70%" height={16} />
                </Box>
                {assignment.courses.map((course: any, colIndex: number) => (
                  <Box
                    key={colIndex}
                    sx={{
                      minWidth: 120,
                      p: 1.5,
                      textAlign: 'center',
                      borderRight:
                        colIndex < assignment.courses.length - 1
                          ? '1px solid'
                          : 'none',
                      borderColor: 'divider',
                    }}
                  >
                    <Skeleton
                      variant="circular"
                      width={16}
                      height={16}
                      sx={{ mx: 'auto' }}
                    />
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{ minWidth: 600 }}>
            {/* Matrix Header */}
            <Box
              sx={{
                display: 'flex',
                bgcolor: 'background.default',
                borderBottom: '1px solid',
                borderColor: 'divider',
                position: 'sticky',
                top: 0,
                zIndex: 1,
              }}
            >
              <Box
                sx={{
                  minWidth: 200,
                  p: 1.5,
                  borderRight: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  User
                </Typography>
              </Box>
              {assignment.courses.map((course: any, index: number) => (
                <Box
                  key={course.id || index}
                  sx={{
                    minWidth: 120,
                    p: 1.5,
                    textAlign: 'center',
                    borderRight:
                      index < assignment.courses.length - 1
                        ? '1px solid'
                        : 'none',
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      lineHeight: 1.2,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {course.name || course.displayName || `Course ${index + 1}`}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Matrix Body */}
            {userProgress.map((user) => (
              <Box
                key={user.userID}
                sx={{
                  display: 'flex',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <Box
                  sx={{
                    minWidth: 200,
                    p: 1.5,
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {user.userName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {user.email}
                  </Typography>
                </Box>
                {assignment.courses.map((course: any, courseIndex: number) => {
                  // Find if this user has completed this course
                  // Based on debug logs: courseProgress has courseID field and status field
                  let courseProgress = user.courseProgress?.find(
                    (cp: any) => cp.courseID === course.id,
                  )

                  // Check completion status - based on debug logs, status can be "NotStarted", "InProgress", "Completed"
                  let isCompleted = courseProgress?.status === 'Completed'

                  return (
                    <Box
                      key={course.id || courseIndex}
                      sx={{
                        minWidth: 120,
                        p: 1.5,
                        textAlign: 'center',
                        borderRight:
                          courseIndex < assignment.courses.length - 1
                            ? '1px solid'
                            : 'none',
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {isCompleted ? (
                        <CorrectIcon
                          sx={{ color: 'success.main', fontSize: 16 }}
                        />
                      ) : (
                        <IncorrectIcon
                          sx={{ color: 'error.main', fontSize: 16 }}
                        />
                      )}
                    </Box>
                  )
                })}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  )
}
